import React, { useState, useEffect } from 'react';
import { Loader2, Receipt, CreditCard, User, Stethoscope, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = ['Efectivo', 'Tarjeta Crédito/Débito', 'Transferencia', 'Seguro Médico', 'Cortesía'];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
  }).format(amount || 0);
};

const SaleModal = ({ 
  isOpen, 
  onClose, 
  patient,
  token,
  onSaleCreated
}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nit_facturacion: 'CF',
    observaciones: ''
  });
  
  // Multiple payments support
  const [payments, setPayments] = useState([
    { metodo: 'Efectivo', monto: '' }
  ]);

  // Pre-fill data when patient changes
  useEffect(() => {
    if (patient && isOpen) {
      setFormData(prev => ({
        ...prev,
        nit_facturacion: patient.nit || 'CF',
      }));
      
      // Reset payments with patient's default payment method
      const defaultMethod = patient.forma_pago || 'Efectivo';
      // If it's Cortesía, total is 0, so set empty payment
      if (defaultMethod === 'Cortesía') {
        setPayments([{ metodo: 'Cortesía', monto: '0' }]);
      } else {
        setPayments([{ metodo: defaultMethod, monto: '' }]);
      }
    }
  }, [patient, isOpen]);

  if (!patient) return null;

  // Get examenes from patient
  const examenes = patient.examenes && patient.examenes.length > 0 
    ? patient.examenes 
    : patient.tipo_examen ? [{ 
        tipo_examen: patient.tipo_examen, 
        ojo_estudiar: patient.ojo_estudiar || '', 
        precio: patient.precio || 0 
      }] : [];

  const subtotal = examenes.reduce((sum, e) => sum + (parseFloat(e.precio) || 0), 0);
  
  // Calculate discount
  let descuentoMonto = 0;
  if (patient.tipo_descuento === 'porcentaje') {
    descuentoMonto = subtotal * (parseFloat(patient.descuento) || 0) / 100;
  } else if (patient.tipo_descuento === 'cantidad') {
    descuentoMonto = parseFloat(patient.descuento) || 0;
  }
  
  // Check if any payment is Cortesía
  const hasCourtesy = payments.some(p => p.metodo === 'Cortesía');
  const total = hasCourtesy ? 0 : (subtotal - descuentoMonto);

  // Calculate total of payments entered
  const totalPagado = payments.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
  const pendiente = total - totalPagado;

  // Add new payment method
  const addPayment = () => {
    // Find a method not already used
    const usedMethods = payments.map(p => p.metodo);
    const availableMethod = PAYMENT_METHODS.find(m => !usedMethods.includes(m) && m !== 'Cortesía') || 'Efectivo';
    setPayments([...payments, { metodo: availableMethod, monto: '' }]);
  };

  // Remove payment method
  const removePayment = (index) => {
    if (payments.length <= 1) return;
    setPayments(payments.filter((_, i) => i !== index));
  };

  // Update payment
  const updatePayment = (index, field, value) => {
    const newPayments = [...payments];
    newPayments[index] = { ...newPayments[index], [field]: value };
    
    // If changing to Cortesía, set amount to 0 and remove other payments
    if (field === 'metodo' && value === 'Cortesía') {
      setPayments([{ metodo: 'Cortesía', monto: '0' }]);
      return;
    }
    
    setPayments(newPayments);
  };

  // Auto-fill remaining amount for single payment
  const autoFillAmount = (index) => {
    if (payments.length === 1 && !payments[0].monto) {
      const newPayments = [...payments];
      newPayments[0].monto = total.toString();
      setPayments(newPayments);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate payments
    if (!hasCourtesy) {
      if (Math.abs(totalPagado - total) > 0.01) {
        toast.error(`El total pagado (${formatCurrency(totalPagado)}) no coincide con el total a cobrar (${formatCurrency(total)})`);
        return;
      }
      
      // Check all payments have amounts
      const invalidPayment = payments.find(p => !p.monto || parseFloat(p.monto) <= 0);
      if (invalidPayment) {
        toast.error('Todos los métodos de pago deben tener un monto válido');
        return;
      }
    }
    
    setSaving(true);

    try {
      // Format payments for the API
      const formasPago = payments.map(p => ({
        metodo: p.metodo,
        monto: parseFloat(p.monto) || 0
      }));
      
      // Primary payment method (first one or the one with highest amount)
      const primaryPayment = payments.reduce((max, p) => 
        (parseFloat(p.monto) || 0) > (parseFloat(max.monto) || 0) ? p : max
      , payments[0]);

      const saleData = {
        patient_id: patient.id,
        nombre_paciente: patient.nombre,
        medico_referente: patient.medico_referente || '',
        examenes: examenes.map(ex => ({
          tipo_examen: ex.tipo_examen,
          ojo_estudiar: ex.ojo_estudiar || '',
          precio: parseFloat(ex.precio) || 0
        })),
        forma_pago: primaryPayment.metodo,
        formas_pago: formasPago, // Array of all payment methods
        nit_facturacion: formData.nit_facturacion || 'CF',
        subtotal: subtotal,
        descuento: descuentoMonto,
        tipo_descuento: patient.tipo_descuento || 'sin_descuento',
        total: total,
        fecha: format(new Date(), 'yyyy-MM-dd'),
        observaciones: formData.observaciones
      };

      await axios.post(`${API_URL}/api/sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Venta registrada exitosamente');
      onClose();
      if (onSaleCreated) onSaleCreated();
    } catch (error) {
      console.error('Error creating sale:', error);
      toast.error('Error al registrar la venta');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sale-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-600" />
            Generar Venta
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Patient Info (Read-only) */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-slate-700">
              <User className="w-4 h-4" />
              <span className="font-medium">{patient.nombre}</span>
            </div>
            {patient.medico_referente && (
              <div className="text-sm text-slate-500">
                Médico Referente: {patient.medico_referente}
              </div>
            )}
          </div>

          {/* Examenes (Read-only) */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-blue-700 font-medium">
              <Stethoscope className="w-4 h-4" />
              Exámenes ({examenes.length})
            </div>
            <div className="space-y-1">
              {examenes.map((exam, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-slate-700">{exam.tipo_examen}</span>
                  <span className="font-medium">{formatCurrency(exam.precio)}</span>
                </div>
              ))}
            </div>
            <div className="pt-2 border-t border-blue-200 flex justify-between">
              <span className="text-blue-700">Subtotal:</span>
              <span className="font-semibold">{formatCurrency(subtotal)}</span>
            </div>
            {descuentoMonto > 0 && (
              <div className="flex justify-between text-sm text-red-600">
                <span>Descuento:</span>
                <span>-{formatCurrency(descuentoMonto)}</span>
              </div>
            )}
          </div>

          {/* NIT for billing */}
          <div className="space-y-2">
            <Label htmlFor="nit_facturacion">NIT para Facturación</Label>
            <Input
              id="nit_facturacion"
              value={formData.nit_facturacion}
              onChange={(e) => setFormData(prev => ({ ...prev, nit_facturacion: e.target.value }))}
              placeholder="CF"
              className="bg-white"
              data-testid="sale-nit"
            />
          </div>

          {/* Payment methods - Combined payments */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Formas de Pago</Label>
              {!hasCourtesy && payments.length < PAYMENT_METHODS.length - 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPayment}
                  className="text-xs h-7"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Agregar método
                </Button>
              )}
            </div>
            
            <div className="space-y-2">
              {payments.map((payment, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Select
                    value={payment.metodo}
                    onValueChange={(value) => updatePayment(index, 'metodo', value)}
                  >
                    <SelectTrigger className="bg-white flex-1" data-testid={`payment-method-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((method) => (
                        <SelectItem 
                          key={method} 
                          value={method}
                          disabled={method !== payment.metodo && payments.some(p => p.metodo === method)}
                        >
                          {method}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {!hasCourtesy && (
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payment.monto}
                      onChange={(e) => updatePayment(index, 'monto', e.target.value)}
                      onFocus={() => autoFillAmount(index)}
                      placeholder="Monto"
                      className="bg-white w-28"
                      data-testid={`payment-amount-${index}`}
                    />
                  )}
                  
                  {payments.length > 1 && !hasCourtesy && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePayment(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-9 w-9"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Payment summary for combined payments */}
            {!hasCourtesy && payments.length > 1 && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total pagado:</span>
                  <span className={`font-medium ${totalPagado >= total ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {formatCurrency(totalPagado)}
                  </span>
                </div>
                {pendiente > 0.01 && (
                  <div className="flex justify-between text-red-600">
                    <span>Pendiente:</span>
                    <span className="font-medium">{formatCurrency(pendiente)}</span>
                  </div>
                )}
                {pendiente < -0.01 && (
                  <div className="flex justify-between text-amber-600">
                    <span>Excedente:</span>
                    <span className="font-medium">{formatCurrency(Math.abs(pendiente))}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Total */}
          <div className={`rounded-lg p-4 flex items-center justify-between ${hasCourtesy ? 'bg-purple-50' : 'bg-emerald-50'}`}>
            <div className="flex items-center gap-2">
              <CreditCard className={`w-5 h-5 ${hasCourtesy ? 'text-purple-600' : 'text-emerald-600'}`} />
              <span className={`font-medium ${hasCourtesy ? 'text-purple-700' : 'text-emerald-700'}`}>
                {hasCourtesy ? 'Cortesía (Sin cargo):' : 'Total a Cobrar:'}
              </span>
            </div>
            <span className={`text-2xl font-bold ${hasCourtesy ? 'text-purple-700' : 'text-emerald-700'}`} data-testid="sale-total">
              {formatCurrency(total)}
            </span>
          </div>
          
          <DialogFooter className="gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-sale-button"
              disabled={saving || (!hasCourtesy && Math.abs(totalPagado - total) > 0.01)}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
              ) : (
                <>
                  <Receipt className="w-4 h-4 mr-2" />
                  Confirmar Venta
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SaleModal;
